import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion, useInView } from "framer-motion";
import { Smartphone, Watch, Laptop, Camera, Gamepad2, Gauge } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { getSectionMedia } from "@/lib/api";
import { computeBadgeLayout, DEVICE_SCALE } from "@/lib/checkLayout";

/* =========================================================
   "Come controlliamo i prodotti"
   Diagramma annotato con sagome accurate e dettagliate
   (iPhone, MacBook, PS5 + DualSense, Apple Watch, mirrorless).
   Ogni numero è collegato con precisione alla componente
   controllata. Al passaggio/click si apre un dettaglio tecnico.
   ========================================================= */

const STROKE = "rgba(245,245,247,0.55)";
const SOFT = "rgba(245,245,247,0.14)";
const FILL = "rgba(198,242,78,0.04)";
const DOT = "rgba(245,245,247,0.45)";

const VW = 320;
const VH = 256;
const DX = 50;
const DY = 18;

function DeviceArt({ id }) {
  const c = { stroke: STROKE, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round", fill: "none" };
  const thin = { stroke: STROKE, strokeWidth: 1, strokeLinecap: "round", strokeLinejoin: "round", fill: "none" };
  switch (id) {
    case "smartwatch":
      return (
        <g {...c}>
          {/* Cinturini */}
          <path d="M92 34 L128 34 L124 62 L96 62 Z" fill={SOFT} />
          <path d="M96 138 L124 138 L128 168 L92 168 Z" fill={SOFT} />
          <circle cx="110" cy="150" r="1" fill={DOT} stroke="none" />
          <circle cx="110" cy="156" r="1" fill={DOT} stroke="none" />
          {/* Cassa */}
          <rect x="80" y="60" width="60" height="80" rx="20" fill={FILL} />
          <rect x="85" y="65" width="50" height="70" rx="15" {...thin} />
          {/* Corona digitale + tasto laterale */}
          <rect x="140" y="86" width="6" height="12" rx="3" fill={SOFT} stroke="none" />
          <rect x="140" y="104" width="5" height="12" rx="2.5" fill={SOFT} stroke="none" />
          {/* Microfono */}
          <circle cx="79" cy="100" r="1" fill={DOT} stroke="none" />
        </g>
      );
    case "pc":
      // MacBook aperto: lid 16:10, notch webcam, cerniera, deck con tastiera/trackpad.
      return (
        <g {...c}>
          <rect x="48" y="30" width="124" height="78" rx="6" fill={FILL} />
          <rect x="52" y="34" width="116" height="70" rx="3" {...thin} />
          <rect x="104" y="30" width="12" height="3.5" rx="1" fill={SOFT} stroke="none" />
          <circle cx="110" cy="31.7" r="0.9" fill={DOT} stroke="none" />
          <path d="M42 109 H178" strokeWidth="2.2" />
          <path d="M38 110 H182 L192 128 H28 Z" fill={FILL} />
          <rect x="60" y="112" width="100" height="8" rx="2" fill={SOFT} stroke="none" />
          <rect x="100" y="121.5" width="20" height="4.5" rx="1.5" {...thin} />
          <path d="M104 110 a6 2.6 0 0 0 12 0" {...thin} />
          <path d="M40 116 h4 M40 120 h4 M176 118 h4" {...thin} />
        </g>
      );
    case "camera":
      // Mirrorless: corpo, pentaprisma, slitta, obiettivo, ghiera, scatto.
      return (
        <g {...c}>
          <rect x="54" y="84" width="112" height="60" rx="10" fill={FILL} />
          <path d="M160 86 q10 0 8 14 l-3 30 q-1 8 -9 8" fill={FILL} />
          <rect x="96" y="68" width="28" height="18" rx="3" fill={SOFT} />
          <rect x="105" y="64" width="12" height="4" rx="1" fill={SOFT} stroke="none" />
          <circle cx="103" cy="114" r="28" />
          <circle cx="103" cy="114" r="20" {...thin} />
          <circle cx="103" cy="114" r="11" fill={SOFT} />
          <circle cx="150" cy="78" r="8" {...thin} />
          <circle cx="156" cy="74" r="3.2" fill={SOFT} stroke="none" />
          <circle cx="132" cy="98" r="2.4" fill={DOT} stroke="none" />
          <path d="M56 106 h5 M56 112 h5" {...thin} />
        </g>
      );
    case "console":
      // PS5 (profilo con lame + slot disco, tasti, base) e controller DualSense.
      return (
        <g {...c}>
          <rect x="62" y="42" width="16" height="110" fill={FILL} />
          <path d="M78 42 C100 48 104 66 98 84 C95 92 90 94 90 98 C90 102 95 104 98 114 C104 132 100 148 78 152 Z" fill={FILL} />
          <path d="M62 42 C40 48 36 66 42 84 C45 92 50 94 50 98 C50 102 45 104 42 114 C36 132 40 148 62 152 Z" fill={FILL} />
          <path d="M70 52 V72" {...thin} />
          <rect x="66" y="120" width="8" height="3" rx="1" fill={SOFT} stroke="none" />
          <circle cx="70" cy="138" r="1.4" fill={DOT} stroke="none" />
          <circle cx="70" cy="144" r="1.4" fill={DOT} stroke="none" />
          <path d="M54 154 H86" strokeWidth="1.8" />
          {/* DualSense */}
          <path d="M140 90 C152 84 178 84 190 90 C202 95 208 106 206 118 C204 128 196 132 188 128 C182 126 178 122 172 120 C166 118 158 118 152 120 C146 122 142 126 136 128 C128 132 120 128 118 118 C116 106 128 96 140 90 Z" fill={FILL} />
          <rect x="156" y="96" width="18" height="12" rx="3" fill={SOFT} />
          <circle cx="148" cy="116" r="6" {...thin} />
          <circle cx="182" cy="116" r="6" {...thin} />
          <path d="M138 102 h6 M141 99 v6" {...thin} />
          <circle cx="188" cy="101" r="1.3" fill={DOT} stroke="none" />
          <circle cx="193" cy="101" r="1.3" fill={DOT} stroke="none" />
          <circle cx="188" cy="107" r="1.3" fill={DOT} stroke="none" />
          <circle cx="193" cy="107" r="1.3" fill={DOT} stroke="none" />
        </g>
      );
    case "phone":
    default:
      // iPhone: proporzioni ~2.2:1, Dynamic Island, tasti, USB-C e speaker.
      return (
        <g {...c}>
          <rect x="77" y="32" width="66" height="150" rx="22" fill={FILL} />
          <rect x="82" y="38" width="56" height="138" rx="17" {...thin} />
          <rect x="97" y="45" width="26" height="8" rx="4" fill={SOFT} stroke="none" />
          <circle cx="103" cy="49" r="1.6" fill={DOT} stroke="none" />
          <circle cx="117" cy="49" r="1.2" fill={DOT} stroke="none" />
          <path d="M75.5 55 v7 M75.5 71 v12 M75.5 87 v12" {...thin} />
          <path d="M144.5 82 v16" {...thin} />
          <rect x="104" y="177" width="12" height="3" rx="1.5" fill={SOFT} stroke="none" />
          <circle cx="90" cy="178.5" r="0.9" fill={DOT} stroke="none" />
          <circle cx="94" cy="178.5" r="0.9" fill={DOT} stroke="none" />
          <circle cx="98" cy="178.5" r="0.9" fill={DOT} stroke="none" />
          <circle cx="122" cy="178.5" r="0.9" fill={DOT} stroke="none" />
          <circle cx="126" cy="178.5" r="0.9" fill={DOT} stroke="none" />
          <circle cx="130" cy="178.5" r="0.9" fill={DOT} stroke="none" />
        </g>
      );
  }
}

const DEVICES = [
  {
    id: "phone",
    label: "Telefono",
    Icon: Smartphone,
    checks: [
      { t: "Display e touch", d: "Analisi pixel per pixel, luminosità uniforme e risposta del tocco su tutta la superficie.", tech: "Rilevamento subpixel spenti, uniformità luminanza ≥ 90%, multitouch a 10 punti e True Tone.", x: 110, y: 108 },
      { t: "Fotocamere", d: "Scatti di prova con ogni ottica: messa a fuoco, stabilizzazione, flash e video.", tech: "Nitidezza MTF, allineamento OIS, gamma dinamica e bilanciamento colore su target calibrato.", x: 103, y: 49 },
      { t: "Salute batteria", d: "Capacità residua e cicli di ricarica misurati con strumenti dedicati.", tech: "Capacità (mAh) e cicli letti dal BMS; sostituzione se salute < 85%.", x: 110, y: 150 },
      { t: "Face ID e impronta", d: "Sblocco biometrico e sensori di prossimità testati in condizioni reali.", tech: "Proiettore IR e sensore ToF: matching biometrico e liveness verificati.", x: 117, y: 49 },
      { t: "Connettività", d: "Wi-Fi, Bluetooth, rete mobile, GPS e NFC: aggancio rapido e segnale stabile.", tech: "RSSI Wi-Fi 6/BT, aggancio 5G/LTE, fix GPS < 30s e lettura NFC.", x: 137, y: 46 },
      { t: "Audio e microfoni", d: "Capsula, altoparlanti e microfoni provati in chiamata e in riproduzione.", tech: "Risposta in frequenza, SPL e cancellazione rumore sui microfoni.", x: 94, y: 178 },
      { t: "Tasti e ricarica", d: "Volume, accensione, tasto muto e porta di ricarica: risposta e tenuta.", tech: "Ciclatura tasti, negoziazione USB-PD e ricarica wireless Qi.", x: 145, y: 90 },
      { t: "Estetica e ripristino", d: "Classificazione estetica reale di scocca e vetro, dati cancellati e account rimosso.", tech: "Grading scocca/vetro, wipe crittografico e sblocco MDM/iCloud.", x: 78, y: 120 },
    ],
  },
  {
    id: "smartwatch",
    label: "Smartwatch",
    Icon: Watch,
    checks: [
      { t: "Display e sempre-attivo", d: "Luminosità, uniformità e risposta del tocco, anche in modalità always-on.", tech: "Luminanza AoD, uniformità OLED e sensibilità al tocco anche bagnato.", x: 110, y: 98 },
      { t: "Sensori di salute", d: "Battito, SpO2, ECG e accelerometro confrontati con strumenti di riferimento.", tech: "PPG, ECG e SpO2 confrontati con reference; IMU calibrato.", x: 110, y: 128 },
      { t: "Corona e tasti", d: "Rotazione della corona digitale e pulsanti laterali fluidi e precisi.", tech: "Encoder della corona e feedback aptico, risposta dei pulsanti.", x: 143, y: 90 },
      { t: "Batteria e ricarica", d: "Autonomia reale e ricarica magnetica: capacità e tempi verificati.", tech: "Capacità residua e ricarica magnetica; autonomia in uso misto.", x: 79, y: 118 },
      { t: "Cinturino e aggancio", d: "Sgancio rapido, tenuta del cinturino e integrità degli attacchi.", tech: "Meccanismo di sgancio e tenuta degli attacchi alla cassa.", x: 110, y: 44 },
      { t: "Resistenza all'acqua", d: "Guarnizioni e sigilli controllati per mantenere la tenuta dichiarata.", tech: "Verifica guarnizioni e tenuta alla profondità dichiarata (ATM).", x: 79, y: 92 },
      { t: "Connettività", d: "Bluetooth, Wi-Fi ed eventuale eSIM: abbinamento stabile con lo smartphone.", tech: "BT/Wi-Fi ed eSIM: stabilità e latenza dell'abbinamento.", x: 143, y: 110 },
      { t: "GPS e ripristino", d: "Localizzazione precisa, dissociazione dall'account e reset completo.", tech: "Precisione GNSS multi-banda e unpairing/reset di fabbrica.", x: 110, y: 76 },
    ],
  },
  {
    id: "pc",
    label: "PC",
    Icon: Laptop,
    checks: [
      { t: "Display e pixel", d: "Pixel difettosi, retroilluminazione uniforme, cerniera e apertura fluida.", tech: "Mappatura pixel/subpixel, delta-E < 2 e uniformità retroilluminazione.", x: 110, y: 66 },
      { t: "Tastiera e trackpad", d: "Ogni tasto, la retroilluminazione e i gesti del trackpad provati uno per uno.", tech: "Test per-tasto, Force Touch e tracking multitouch del trackpad.", x: 110, y: 116 },
      { t: "Batteria e cicli", d: "Capacità residua e cicli di carica: autonomia reale misurata sotto carico.", tech: "Cicli e capacità dal SMC; autonomia stimata sotto carico reale.", x: 150, y: 118 },
      { t: "Prestazioni", d: "Stress test di CPU, GPU e RAM per verificare stabilità e temperature.", tech: "Stress CPU/GPU, controllo throttling termico e stabilità RAM.", x: 78, y: 66 },
      { t: "Archiviazione SSD", d: "Salute del disco, velocità di lettura/scrittura e settori integri.", tech: "SMART, TBW residui e throughput sequenziale/random.", x: 140, y: 66 },
      { t: "Porte e connettori", d: "USB, USB-C, HDMI, jack audio e lettore schede provati con periferiche reali.", tech: "Thunderbolt/USB-C in PD e DisplayPort, MagSafe e jack cuffie.", x: 41, y: 116 },
      { t: "Webcam e audio", d: "Fotocamera, microfoni e altoparlanti testati in videochiamata.", tech: "Sensore FaceTime, array microfonico e speaker stereo.", x: 110, y: 31.7 },
      { t: "Ventole e ripristino", d: "Rumorosità e raffreddamento sotto carico, sistema pulito e reinstallato.", tech: "Curva ventole e temperature, reinstallazione OS pulita.", x: 70, y: 118 },
    ],
  },
  {
    id: "camera",
    label: "Fotocamera",
    Icon: Camera,
    checks: [
      { t: "Sensore e immagine", d: "Analisi del sensore: pixel caldi o morti, polvere e resa cromatica.", tech: "Hot/dead pixel, dust map e resa colore su target di riferimento.", x: 103, y: 114 },
      { t: "Otturatore e scatti", d: "Numero reale di scatti effettuati e funzionamento a ogni velocità.", tech: "Shutter count e precisione dei tempi a ogni velocità.", x: 156, y: 74 },
      { t: "Autofocus", d: "Messa a fuoco automatica e manuale, tracking del soggetto e stabilizzazione.", tech: "Precisione AF a rilevamento di fase e tracking del soggetto.", x: 132, y: 98 },
      { t: "Mirino e schermo", d: "Mirino elettronico e LCD orientabile: luminosità, touch e assenza di aloni.", tech: "EVF e LCD: luminanza, assenza di tearing e touch.", x: 110, y: 72 },
      { t: "Ghiere e pulsanti", d: "Ghiere di controllo, joystick e tasti personalizzabili fluidi e precisi.", tech: "Encoder delle ghiere e ciclatura dei comandi.", x: 150, y: 78 },
      { t: "Innesto e obiettivo", d: "Baionetta, contatti elettronici e tenuta dell'innesto verificati.", tech: "Contatti baionetta e comunicazione elettronica con l'ottica.", x: 78, y: 114 },
      { t: "Schede e batteria", d: "Slot SD/CFexpress, ricarica e autonomia della batteria controllati.", tech: "Slot SD/CFexpress e capacità della batteria verificate.", x: 103, y: 142 },
      { t: "Connessioni e firmware", d: "USB, HDMI, Wi-Fi e firmware aggiornato, impostazioni riportate a zero.", tech: "USB/HDMI, Wi-Fi e firmware aggiornato all'ultima release.", x: 57, y: 109 },
    ],
  },
  {
    id: "console",
    label: "Console",
    Icon: Gamepad2,
    checks: [
      { t: "Accensione e alimentazione", d: "Avvio, alimentatore e assorbimento verificati per un funzionamento stabile.", tech: "PSU e assorbimento, ciclo avvio/standby e integrità firmware.", x: 70, y: 138 },
      { t: "Lettore dischi", d: "Lettura di giochi e dischi, silenziosità e riconoscimento dei supporti.", tech: "Lettura BD/UHD, calibrazione laser e rumorosità.", x: 70, y: 60 },
      { t: "Uscita video", d: "HDMI e risoluzioni fino al massimo supportato, HDR e frame rate corretti.", tech: "HDMI 2.1: 4K120, VRR e HDR su pattern di riferimento.", x: 70, y: 45 },
      { t: "Porte e connettori", d: "USB, rete LAN e uscite audio provati con periferiche reali.", tech: "USB-A/C, LAN Gigabit e uscita audio verificate.", x: 70, y: 121 },
      { t: "Raffreddamento", d: "Ventole e temperature sotto carico prolungato per evitare surriscaldamenti.", tech: "Ventola e liquid metal: temperature sotto carico prolungato.", x: 92, y: 96 },
      { t: "Controller: stick", d: "Analisi del drift, precisione degli stick analogici e ritorno della vibrazione.", tech: "Deriva stick (deadzone), linearità assi e feedback aptico.", x: 148, y: 116 },
      { t: "Tasti e grilletti", d: "Pulsanti, dorsali e grilletti analogici testati uno per uno.", tech: "Grilletti adattivi L2/R2 e ciclatura dei pulsanti.", x: 190, y: 104 },
      { t: "Storage e ripristino", d: "Salute della memoria interna, disconnessione dell'account e reset.", tech: "Salute SSD NVMe, dissociazione account e reset di fabbrica.", x: 94, y: 74 },
    ],
  },
];

const markerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

function Marker({ m, n, active, onSelect, animated }) {
  const r = active ? 13 : 11;
  const Wrap = animated ? motion.g : "g";
  const wrapProps = animated ? { variants: markerVariants } : {};
  return (
    <Wrap className="cursor-pointer" onClick={onSelect} onMouseEnter={onSelect} {...wrapProps}>
      <line x1={m.mx} y1={m.my} x2={m.px} y2={m.py} stroke="#C6F24E" strokeOpacity={active ? 1 : 0.5} strokeWidth={active ? 1.8 : 1.2} />
      <circle cx={m.px} cy={m.py} r={active ? 3.6 : 2.4} fill="#C6F24E" fillOpacity={active ? 1 : 0.8} />
      {active && <circle cx={m.px} cy={m.py} r="6.5" fill="none" stroke="#C6F24E" strokeOpacity="0.4" strokeWidth="1" />}
      <circle cx={m.mx} cy={m.my} r={r} fill={active ? "#C6F24E" : "#0A0A0B"} stroke="#C6F24E" strokeOpacity={active ? 0 : 0.55} strokeWidth="1.2" />
      <text x={m.mx} y={m.my} dy="0.34em" textAnchor="middle" fontSize="12" fontWeight="700" fill={active ? "#0A0A0B" : "rgba(245,245,247,0.82)"} style={{ pointerEvents: "none" }}>
        {n}
      </text>
    </Wrap>
  );
}

export function HowWeCheck({ title = "Come controlliamo i prodotti", subtitle = "Ogni dispositivo passa da una stazione di controllo dedicata. Passa il mouse o tocca un controllo per vedere, punto per punto, dove interveniamo." }) {
  const reduce = useReducedMotion();
  const [device, setDevice] = useState(0);
  const [active, setActive] = useState(0);
  const [hover, setHover] = useState(false);
  const [media, setMedia] = useState({});
  const [phase, setPhase] = useState("idle"); // idle -> scan -> reveal
  const timer = useRef(null);
  const stageRef = useRef(null);
  const inView = useInView(stageRef, { amount: 0.45 });

  useEffect(() => {
    getSectionMedia().then((d) => setMedia(d?.devices || {})).catch(() => {});
  }, []);

  const current = DEVICES[device];
  const checks = current.checks;
  const total = checks.length;
  const deviceMedia = media[current.id] || {};
  const customPoints = deviceMedia.points || {};

  // Punti in percentuale (0-100): dal DB se presenti, altrimenti dalle coordinate di default.
  const ptsPct = useMemo(
    () => checks.map((c, i) => {
      const cp = customPoints[i] || customPoints[String(i)];
      if (cp && cp.x != null) return { x: Number(cp.x), y: Number(cp.y), bx: cp.bx, by: cp.by };
      return { x: ((c.x + DX) / VW) * 100, y: ((c.y + DY) / VH) * 100 };
    }),
    [checks, customPoints]
  );
  const badgeDefaults = useMemo(() => computeBadgeLayout(ptsPct.map((p) => ({ x: p.x, y: p.y }))), [ptsPct]);
  const markers = useMemo(() => {
    const m = {};
    ptsPct.forEach((p, i) => {
      const bx = p.bx != null ? Number(p.bx) : badgeDefaults[i].bx;
      const by = p.by != null ? Number(p.by) : badgeDefaults[i].by;
      m[i] = { px: (p.x / 100) * VW, py: (p.y / 100) * VH, mx: (bx / 100) * VW, my: (by / 100) * VH };
    });
    return m;
  }, [ptsPct, badgeDefaults]);
  const activeCheck = checks[active];

  // Con reduced-motion mostra tutto subito, senza scanner.
  const showMarkers = reduce || phase === "reveal";

  // Avvia/riavvia la sequenza scanner ogni volta che la sezione entra in vista.
  useEffect(() => {
    if (reduce) { setPhase("reveal"); return; }
    if (inView) setPhase((p) => (p === "idle" ? "scan" : p));
    else setPhase("idle");
  }, [inView, reduce]);

  // Il ciclo automatico dei controlli parte solo dopo che i punti sono comparsi.
  useEffect(() => {
    if (hover || reduce || phase !== "reveal") return;
    timer.current = setInterval(() => setActive((a) => (a + 1) % total), 3200);
    return () => clearInterval(timer.current);
  }, [hover, reduce, total, device, phase]);

  const goDevice = (i) => {
    setDevice(i);
    setActive(0);
    // Riproduci lo scanner per il nuovo dispositivo.
    if (!reduce) setPhase("scan");
  };

  const scanRegion = { left: "16%", width: "68%", top: "5%", height: "90%" };

  return (
    <section className="section-tight" data-testid="home-how-we-check">
      <div className="container-page">
        <Reveal className="max-w-2xl">
          <span className="eyebrow" data-testid="check-eyebrow">
            <span className="rn-pulse mr-2 inline-block h-1.5 w-1.5 rounded-full bg-lime" />
            Il metodo RINOVA
          </span>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {title}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            {subtitle}
          </p>
        </Reveal>

        <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Dispositivi">
          {DEVICES.map((d, i) => {
            const on = i === device;
            return (
              <button
                key={d.id}
                role="tab"
                aria-selected={on}
                onClick={() => goDevice(i)}
                data-testid={`check-device-tab-${d.id}`}
                className={[
                  "inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-200",
                  on ? "bg-lime text-ink" : "bg-surface2 text-muted-foreground hover:bg-surface3 hover:text-foreground",
                ].join(" ")}
              >
                <d.Icon className="h-4 w-4" strokeWidth={2} />
                {d.label}
              </button>
            );
          })}
        </div>

        <div
          className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          {/* Diagramma */}
          <div ref={stageRef} className="relative overflow-hidden rounded-2xl bg-surface2 p-3 sm:p-5" data-testid="check-stage">
            <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(198,242,78,0.07), transparent 68%)" }} />
            <div className="relative mx-auto aspect-[320/256] w-full max-w-[600px]">
              {deviceMedia.image && (
                <img src={deviceMedia.image} alt={current.label} className="pointer-events-none absolute inset-0 h-full w-full object-contain" style={{ transform: `scale(${DEVICE_SCALE})`, transformOrigin: "center" }} />
              )}

              {/* Scanner: passa una sola volta, poi sparisce lasciando comparire i punti */}
              <AnimatePresence>
                {!reduce && phase === "scan" && (
                  <motion.div
                    key="scanner"
                    className="pointer-events-none absolute overflow-hidden rounded-xl"
                    style={scanRegion}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  >
                    {/* Area già scansionata che si "riempie" dall'alto */}
                    <motion.div
                      className="absolute inset-x-0 top-0"
                      initial={{ height: "0%" }}
                      animate={{ height: "100%" }}
                      transition={{ duration: 1.9, ease: "easeInOut" }}
                      style={{ background: "linear-gradient(to bottom, rgba(198,242,78,0.10), rgba(198,242,78,0.02))" }}
                    />
                    {/* Griglia di scansione tenue */}
                    <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "linear-gradient(rgba(198,242,78,0.10) 1px, transparent 1px)", backgroundSize: "100% 22px" }} />
                    {/* Fascio in movimento */}
                    <motion.div
                      className="absolute inset-x-0 h-24 -translate-y-1/2"
                      initial={{ top: "0%" }}
                      animate={{ top: "100%" }}
                      transition={{ duration: 1.9, ease: "easeInOut" }}
                      onAnimationComplete={() => setPhase("reveal")}
                    >
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent, rgba(198,242,78,0.22), transparent)" }} />
                      <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 bg-lime" style={{ boxShadow: "0 0 22px 3px rgba(198,242,78,0.85)" }} />
                      <div className="absolute inset-x-0 top-[calc(50%-7px)] h-px bg-lime/40" />
                      <div className="absolute inset-x-0 top-[calc(50%+7px)] h-px bg-lime/40" />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.svg
                  key={current.id}
                  viewBox={`0 0 ${VW} ${VH}`}
                  className="absolute inset-0 h-full w-full"
                  initial={reduce ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={reduce ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <g transform={`translate(${VW / 2} ${VH / 2}) scale(${DEVICE_SCALE}) translate(${-VW / 2} ${-VH / 2})`}>
                    <g transform={`translate(${DX} ${DY})`}>
                      {!deviceMedia.image && <DeviceArt id={current.id} />}
                    </g>
                  </g>
                  {/* I marker compaiono uno dopo l'altro (stagger) dopo la scansione */}
                  <motion.g
                    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.13, delayChildren: 0.05 } } }}
                    initial={reduce ? "show" : "hidden"}
                    animate={showMarkers ? "show" : "hidden"}
                  >
                    {checks.map((c, i) =>
                      markers[i] ? (
                        <Marker key={c.t} m={markers[i]} n={i + 1} active={i === active} onSelect={() => setActive(i)} animated />
                      ) : null
                    )}
                  </motion.g>
                </motion.svg>
              </AnimatePresence>
            </div>
          </div>

          {/* Pannello */}
          <div className="flex flex-col rounded-2xl bg-surface2 p-6 sm:p-8">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
              <current.Icon className="h-4 w-4 text-lime" strokeWidth={2} />
              {current.label}
              <span className="font-normal text-muted-foreground">· {total} controlli</span>
            </span>

            <div className="mt-5 min-h-[196px] sm:min-h-[208px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`head-${current.id}-${active}`}
                  initial={reduce ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, y: -6 }}
                  transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-3xl font-extrabold leading-none text-lime tabular-nums">
                      {String(active + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-2xl font-extrabold tracking-tight text-foreground" data-testid="check-active-title">
                      {activeCheck.t}
                    </h3>
                  </div>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{activeCheck.d}</p>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <motion.div
                  key={`tech-${current.id}-${active}`}
                  initial={reduce ? false : { opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={reduce ? undefined : { opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 flex gap-2.5 rounded-xl border border-lime/20 bg-lime/[0.06] px-4 py-3" data-testid="check-tech">
                    <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-lime" strokeWidth={2} />
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-lime">Dettaglio tecnico</div>
                      <p className="mt-1 text-[13px] leading-relaxed text-foreground/85">{activeCheck.tech}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {checks.map((c, i) => {
                const on = i === active;
                return (
                  <button
                    key={c.t}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    data-testid={`check-item-${current.id}-${i}`}
                    className={[
                      "flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200",
                      on ? "bg-white/[0.06]" : "hover:bg-white/[0.04]",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold tabular-nums transition-colors duration-200",
                        on ? "bg-lime text-ink" : "bg-white/[0.06] text-muted-foreground",
                      ].join(" ")}
                    >
                      {i + 1}
                    </span>
                    <span className={["text-sm font-medium leading-tight transition-colors duration-200", on ? "text-foreground" : "text-muted-foreground"].join(" ")}>
                      {c.t}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
